import { useMutation } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { useState, type FormEvent } from 'react'

import { FIELD_LABELS } from '@brickup/shared/locales/de'
import { AUTH } from '@brickup/shared/locales/de/auth'
import { VALIDATION } from '@brickup/shared/locales/de/validation'
import { forgotPassword } from '@/api/auth'
import { TextInput } from '@/components/base'
import {
  PublicPageActions,
  PublicPageBackButton,
  PublicPageCard,
  PublicPageCardMessage,
  PublicPageHeading,
} from '@/components/Public/PublicPageCard'
import { SnackbarType } from '@/components/Snackbar'
import { useSnackbar } from '@/hooks/use-snackbar'

export const Route = createFileRoute('/forgot-password')({
  component: ForgotPasswordScreen,
})

function ForgotPasswordScreen() {
  const [email, setEmail] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const addSnackbar = useSnackbar()

  // Both outcomes report the same thing: whether an address is registered is
  // not something an unauthenticated caller gets to find out.
  const { mutate, isPending } = useMutation({
    mutationFn: () => forgotPassword(email),
    onSuccess: () => setSubmitted(true),
    onError: () => setSubmitted(true),
  })

  const onSubmit = (event: FormEvent) => {
    event.preventDefault()
    if (!email) {
      addSnackbar({ title: VALIDATION.invalid.email, type: SnackbarType.ERROR })
      return
    }
    mutate()
  }

  if (submitted) {
    return (
      <PublicPageCardMessage
        title={AUTH.resetLinkSent}
        description={AUTH.resetLinkSentDescription}
      >
        <PublicPageBackButton />
      </PublicPageCardMessage>
    )
  }

  return (
    <PublicPageCard>
      <PublicPageHeading
        title={AUTH.forgotPasswordTitle}
        description={AUTH.forgotPasswordDescription}
      />

      <form onSubmit={onSubmit} className="flex flex-col gap-3">
        <TextInput
          type="email"
          autoComplete="username"
          placeholder={FIELD_LABELS.email.label}
          value={email}
          disabled={isPending}
          onValueChange={value => setEmail(value ?? '')}
        />

        <PublicPageActions label={AUTH.sendResetLink} loading={isPending} />
      </form>
    </PublicPageCard>
  )
}
